import { useEffect, useState, type ReactNode } from 'react';
import type { HouseInfo } from '@guest/shared';
import { guestApi } from '../lib/api.js';
import { Icon } from '../lib/icons.js';
import { AppBar } from '../components/Chrome.js';
import { Panel } from '../components/ui.js';

/**
 * Bodies are plain text, deliberately: the owner is writing this on a phone,
 * not learning markdown. Blank lines split paragraphs and lines starting "- "
 * become bullets. That is the whole grammar.
 */
function Body({ text }: { text: string }): ReactNode {
  const blocks = text.trim().split(/\n\s*\n/);
  return (
    <>
      {blocks.map((block, i) => {
        const lines = block.split('\n');
        const bullets = lines.filter((l) => l.trimStart().startsWith('- '));
        if (bullets.length === lines.length) {
          return (
            <ul className="info-list" key={i}>
              {lines.map((l, j) => (
                <li key={j}>{l.trimStart().slice(2)}</li>
              ))}
            </ul>
          );
        }
        return (
          <p className="info-para" key={i}>
            {block}
          </p>
        );
      })}
    </>
  );
}

export function Info(): ReactNode {
  const [info, setInfo] = useState<HouseInfo | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void guestApi
      .info()
      .then(setInfo)
      .catch(() => setError('These notes are not available to you.'));
  }, []);

  if (error) return <div className="empty">{error}</div>;
  if (!info) return <div className="empty">Loading…</div>;

  const empty = info.contacts.length === 0 && info.sections.length === 0;

  return (
    <>
      <AppBar title="House notes" subtitle="While you are looking after the place" />
      <main className="shell stack" style={{ gap: 'var(--space-3)' }}>
        {empty && <div className="empty">Nothing has been written yet.</div>}

        {info.contacts.length > 0 && (
          <Panel title="Who to call" icon="info" className="stack">
            <div className="contacts">
              {info.contacts.map((c) => (
                <a
                  key={c.id}
                  className="contact"
                  href={`tel:${c.phone.replace(/[^+0-9]/g, '')}`}
                  aria-label={`Call ${c.name || c.role}`}
                >
                  <span className="glyph">
                    <Icon name="key" size={17} />
                  </span>
                  <span className="who">
                    <span className="nm">{c.name || c.role}</span>
                    <span className="rl">
                      {c.name && c.role ? c.role : ''}
                      {c.note ? `${c.name && c.role ? ' · ' : ''}${c.note}` : ''}
                    </span>
                  </span>
                  <span className="num">{c.phone}</span>
                </a>
              ))}
            </div>
          </Panel>
        )}

        {info.sections.map((s) => (
          <Panel key={s.id} title={s.title} icon={s.icon} className="stack">
            <Body text={s.body} />
          </Panel>
        ))}
      </main>
    </>
  );
}
